import { useEffect, useState } from "react";

import { Icon_Comment24, isLoggedIn, Spinner } from "@shapr3d/frontend-lib";
import classNames from "classnames";

import { type CommentID, useComments } from "../../hooks/useComments";
import { useTracker } from "../../hooks/useTracker";
import { useWebExport } from "../../hooks/useWebExport";
import { useWindowSize, ViewportType } from "../../hooks/useWindowSize";
import { useCommentsStore } from "../../stores/commentStore";
import { Comment } from "../Comment/Comment";
import { Sidebar } from "../Sidebar/Sidebar";
import { ToolButton } from "../ToolButton/ToolButton";

import style from "./CommentSidebar.module.css";

interface CommentSidebarProps {
  isOpen: boolean;
  isDisabled?: boolean;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CommentSidebar = ({ isOpen, setIsOpen, isDisabled }: CommentSidebarProps) => {
  const tracker = useTracker();
  const {
    data: commentsData,
    isSuccess: isCommentsSuccess,
    isError: isCommentsFetchError,
    isLoading: isCommentsLoading,
  } = useComments();
  const {
    data: webExportData,
    isSuccess: isWebExportSuccess,
    isLoading: isWebExportLoading,
    viewLinkId,
  } = useWebExport();
  const { setOpenCommentId, openCommentId } = useCommentsStore();
  const { viewportType } = useWindowSize();

  const handleCommentClick = (commentId: CommentID) => {
    tracker.trackCommentSelected({
      commentId,
      webExportId: webExportData?.webExport.webExportId,
      source: "sidebar",
      viewLinkId: viewLinkId ?? "",
    });

    setOpenCommentId(commentId);
  };

  const isLoaded = isCommentsSuccess && isWebExportSuccess;
  const isPrivateCloud = Boolean(webExportData?.isPrivateCloud);
  const isCommentingEnabled = Boolean(webExportData?.isCommentingEnabled);

  // Open the sidebar by default
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  useEffect(() => {
    if (
      isCommentsSuccess &&
      isFirstFetch &&
      viewportType !== ViewportType.Mobile &&
      isCommentingEnabled
    ) {
      tracker.trackCommentingDisplayed({
        viewLinkId: viewLinkId ?? "",
        webExportId: webExportData?.webExport.webExportId,
      });
      setIsFirstFetch(false);
      setIsOpen?.(true);
    }
  }, [
    isCommentsSuccess,
    isFirstFetch,
    setIsOpen,
    setIsFirstFetch,
    tracker,
    viewLinkId,
    viewportType,
    isCommentingEnabled,
    webExportData,
  ]);

  return (
    <Sidebar
      title="Comments"
      position="right"
      isOpen={isOpen}
      onClose={() => setIsOpen?.(false)}
      controlButtons={
        !isPrivateCloud ? (
          <ToolButton
            title="Comments"
            alt="Comments"
            icon={<Icon_Comment24 />}
            onClick={() => {
              setIsOpen?.((isOpen) => {
                const newValue = !isOpen;
                tracker.trackCommentingSidebarVisibilityChanged({
                  viewLinkId: viewLinkId ?? "",
                  visible: newValue,
                });
                return newValue;
              });
            }}
            isActive={isOpen}
            isDisabled={isDisabled}
          />
        ) : undefined
      }
    >
      {!isLoggedIn() && webExportData?.baseRole === "viewer" && (
        <div className={style.emptyContent}>
          <div className={style.text}>
            Gather feedback on your designs!
            <br></br>
            Log in to manage sharing settings and enable commenting.
          </div>
        </div>
      )}

      {isCommentsFetchError && (
        <div className={style.emptyContent}>
          <div className={style.text}>
            Something went wrong while loading comments. Please try again later.
          </div>
        </div>
      )}

      {(isWebExportLoading || isCommentsLoading) && isLoggedIn() && (
        <div className={style.emptyContent}>
          <Spinner size={20} />
        </div>
      )}

      {isLoaded && !commentsData?.comments?.length && (
        <div className={style.emptyContent}>
          <div className={style.text}>
            No comments yet. To place a comment use the Add Comment tool or Ctrl+Shift+click on the
            model.
          </div>
        </div>
      )}

      {commentsData?.comments?.map((comment) => {
        return (
          <div
            key={comment.commentId}
            className={style.commentSidebarBox}
            onClick={() => handleCommentClick(comment.commentId)}
          >
            <div
              className={classNames(
                style.commentSidebarCard,
                comment.commentId === openCommentId && style.active
              )}
            >
              <Comment
                isTruncated
                initialMode="view"
                title={commentsData?.users[comment.createdByUserId] ?? "Unknown user"}
                date={comment.createdAt}
                actions={<Comment.ContextMenu comment={comment} />}
              >
                {comment.message}
              </Comment>
            </div>
          </div>
        );
      })}
    </Sidebar>
  );
};
